import React, { useState } from 'react';
import { CartItem } from '../hooks/useCart';
import './ProductModal.css';

interface Product {
  id: string;
  name: string;
  description?: string;
  price: string;
  images: string[];
  size?: string;
  category?: string;
  stock_quantity?: number;
  tags?: string[];
}

interface ProductModalProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (item: Omit<CartItem, 'quantity'>) => void;
}

const ProductModal: React.FC<ProductModalProps> = ({ product, isOpen, onClose, onAddToCart }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  if (!isOpen || !product) return null;

  const handleAddToCart = () => {
    onAddToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images?.[0] || '/images/placeholder.jpg'
    });
    onClose();
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === (product.images?.length || 1) - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0 ? (product.images?.length || 1) - 1 : prev - 1
    );
  };

  const formatPrice = (price: string) => {
    const numPrice = parseFloat(price.replace('$', '').replace(',', ''));
    return `$${numPrice.toFixed(2)}`;
  };

  return (
    <div className="product-modal-overlay" onClick={onClose}>
      <div className="product-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>×</button>

        <div className="product-modal-content">
          {/* Image Gallery */}
          <div className="product-gallery">
            <div className="main-image-container">
              <button className="nav-btn prev-btn" onClick={prevImage}>‹</button>
              <img
                src={product.images?.[selectedImageIndex] || '/images/placeholder.jpg'}
                alt={product.name}
                className="main-product-image"
              />
              <button className="nav-btn next-btn" onClick={nextImage}>›</button>
            </div>

            {/* Thumbnail images */}
            {product.images && product.images.length > 1 && (
              <div className="thumbnail-gallery">
                {product.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${product.name} ${index + 1}`}
                    className={`thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="product-details">
            <h1 className="product-title">{product.name}</h1>

            <div className="product-price">
              {formatPrice(product.price)}
            </div>

            {product.size && (
              <div className="product-size">
                <strong>Size:</strong> {product.size}
              </div>
            )}

            {product.category && (
              <div className="product-category">
                <strong>Category:</strong> {product.category}
              </div>
            )}

            {product.stock_quantity !== undefined && (
              <div className="product-stock">
                <strong>Stock:</strong> {product.stock_quantity > 0 ? `${product.stock_quantity} available` : 'Out of stock'}
              </div>
            )}

            {product.description && (
              <div className="product-description">
                <h3>Description</h3>
                <p>{product.description}</p>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="product-tags">
                <h3>Tags</h3>
                <div className="tags-list">
                  {product.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Add to Cart Section */}
            <div className="add-to-cart-section">
              <div className="quantity-selector">
                <label>Quantity:</label>
                <div className="quantity-controls">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <span>{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    disabled={product.stock_quantity !== undefined && quantity >= product.stock_quantity}
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                className="add-to-cart-modal-btn"
                onClick={handleAddToCart}
                disabled={product.stock_quantity === 0}
              >
                {product.stock_quantity === 0 ? 'Out of Stock' : `Add to Cart - ${formatPrice(product.price)}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
